/**
 * Represents a singleton class that provides operations for tables.
 * @class
 * @classdesc A class handling all table related operations on API response and data stored on client store.
 * @private
 * @author Abhinay Thakur <abhinay@gohighlevel.com>
 */
export class TableOperation {
  /**
   * Private static instance of the TableOperation class.
   * @type {TableOperation}
   * @private
   */
  private static _instance: TableOperation

  /**
   * Creates an instance of TableOperation.
   * @private
   * @constructor
   * @throws {Error} Throws an error if an instance of the class already exists.
   */
  private constructor() {
    // Prevents from creating multiple instances of the class.
    if (TableOperation._instance)
      throw new Error('Cannot instantiate a singleton class again.')

    // Initialize the class variables.

    // Set the class instance.
    TableOperation._instance = this
  }

  /**
  * Get a paginated collection based on the provided pagination configuration.
  *
  * @param {object} paginationConfig - The pagination configuration object.
  * @param {Array} collection - The copy of original collection to paginate.
  * @returns {Array} The paginated subset of the original collection.
  * @example
  * import tableOperation from '../common/table-operation'
  * const result = tableOperation.getPaginatedCollection({ paginationConfig, [...collection] });
  */
  public getPaginatedCollection({ paginationConfig, collection }) {
    const startIndex = (paginationConfig.page - 1) * paginationConfig.limit
    const endIndex =
      collection.length - startIndex < paginationConfig.limit
        ? collection.length
        : startIndex + paginationConfig.limit

    return collection.slice(startIndex, endIndex)
  }

  /**
  * Apply client-side filtering to a collection based on search query and filter conditions.
  *
  * @param {object} params - The parameters for filtering.
  * @param {string} params.searchQuery - The search query to filter by.
  * @param {Array} params.filterConditions - An array of filter condition functions.
  * @param {Array} params.collection - The collection to be filtered.
  * @returns {Array} The filtered subset of the original collection.
  * @example
  * // Usage example
  * const filteredResults = applyClientSideFilter({
  *   searchQuery: 'keyword',
  *   filterConditions: [condition1, condition2],
  *   collection: originalCollection
  * });
  */
  public applyClientSideFilter ({searchQuery, filterConditions, collection}) {
    if (!searchQuery)
      return collection

    const filteredCollection = collection.filter((item) => {
      const hasAtleastOneTrue = filterConditions
        ?.map((condition) => condition(item, searchQuery))
        ?.some((result) => result === true)

      return hasAtleastOneTrue
    })

    return filteredCollection
  }

  /**
   * Gets the singleton instance of the TableOperation class.
   * If the instance does not exist, it creates a new one.
   *
   * @returns {TableOperation} The singleton instance of the TableOperation class.
   */
  public static getInstance(): TableOperation {
    if (!TableOperation._instance) {
      TableOperation._instance = new TableOperation()
    }

    return TableOperation._instance
  }
}

export default TableOperation.getInstance()
